'use client';

import type { UserApiKeyEntity } from '@wolfejs/ms-api-js/src/types/users';
import { useGenerateApiKey, useRevokeApiKey } from '@wolfejs/ms-api-react';
import { Button } from '@wolfejs/ui/Button';
import { Textarea } from '@wolfejs/ui/Textarea';
import { Title } from '@wolfejs/ui/Title';
import { UI } from '@wolfejs/ui/UI';
import type { UIComponentProps } from '@wolfejs/ui/types/UI';
import { cn } from '@wolfejs/ui/utils/classnames';
import { useEffect, useState } from 'react';

export type AdminApiTokenProps = UIComponentProps & {
  userApiKey?: Partial<UserApiKeyEntity>;
};
export const AdminApiTokenForm = ({ className, userApiKey }: AdminApiTokenProps) => {
  const generateApiKey = useGenerateApiKey();
  const revokeApiKey = useRevokeApiKey();
  const [apiKey, setApiKey] = useState<Partial<UserApiKeyEntity> | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!userApiKey) return;
    setApiKey(userApiKey);
  }, [userApiKey]);

  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      const response = await generateApiKey.mutateAsync(!!apiKey);
      if (response) {
        setApiKey(response.data);
        setTimeout(() => setApiKey({ id: response.data.id }), 1000 * 60);
      }
    } catch (error) {
      UI.notifications.error(`There was an error during Api-Key generation process. ${error}`);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async () => {
    setIsLoading(true);
    try {
      if (apiKey) {
        await revokeApiKey.mutateAsync();
        setApiKey(undefined);
      }
    } catch (error) {
      UI.notifications.error(`There was an error during revoke Api-Key process. ${error}`);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Title as="h3">API Key Management</Title>
      <div className={cn('@container grid grid-cols-2 gap-4 lg:grid-cols-2', className)}>
        {apiKey && apiKey.token && (
          <>
            <div className="flex flex-col gap-2">
              <Textarea size="md" value={apiKey.token} />
            </div>
          </>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex gap-4">
            {apiKey && apiKey.id ? (
              <>
                <Button color="error" size="md" onClick={() => handleRevoke()} loading={isLoading}>
                  Revoke Key
                </Button>
              </>
            ) : (
              <>
                <Button color="primary" size="md" onClick={() => handleGenerate()} loading={isLoading}>
                  Generate Key
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
